import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

const stepKey = (step) => step.n || step.id;

const WorkflowPreviewModal = ({ step, steps, onClose, onNavigate }) => {
    const closeRef = useRef(null);
    const key = stepKey(step);
    const currentIndex = steps.findIndex((s) => stepKey(s) === key);

    const goPrev = useCallback(() => {
        if (currentIndex > 0) {
            onNavigate(steps[currentIndex - 1]);
        }
    }, [currentIndex, onNavigate, steps]);

    const goNext = useCallback(() => {
        if (currentIndex < steps.length - 1) {
            onNavigate(steps[currentIndex + 1]);
        }
    }, [currentIndex, onNavigate, steps]);

    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            } else if (event.key === "ArrowLeft") {
                goPrev();
            } else if (event.key === "ArrowRight") {
                goNext();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [goNext, goPrev, onClose, key]);

    const title = step.t || step.title;
    const description = step.d || step.detail;

    return createPortal(
        <div className="clk clk-workflow-modal" role="presentation">
            <button
                type="button"
                className="clk-workflow-modal-backdrop"
                aria-label="Close preview"
                onClick={onClose}
            />
            <div
                className="clk-workflow-modal-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`clk-workflow-modal-title-${key}`}
            >
                <div className="clk-workflow-modal-toolbar">
                    <div className="clk-workflow-modal-meta">
                        {step.n && <span className="clk-workflow-modal-num">{step.n}</span>}
                        <h3 id={`clk-workflow-modal-title-${key}`}>{title}</h3>
                    </div>
                    <button
                        ref={closeRef}
                        type="button"
                        className="clk-workflow-modal-close"
                        aria-label="Close preview"
                        onClick={onClose}
                    >
                        <Icon name="close" size={18} />
                    </button>
                </div>

                <figure className="clk-workflow-modal-figure">
                    <img
                        src={step.image}
                        alt={step.imageAlt || ""}
                        className="clk-workflow-modal-img"
                        loading="eager"
                        decoding="sync"
                    />
                </figure>

                <div className="clk-workflow-modal-footer">
                    {description && <p>{description}</p>}
                    <div className="clk-workflow-modal-nav">
                        <button
                            type="button"
                            className="clk-workflow-modal-nav-btn"
                            onClick={goPrev}
                            disabled={currentIndex <= 0}
                            aria-label="Previous"
                        >
                            <Icon name="arrow" size={16} className="clk-workflow-modal-nav-prev" />
                        </button>
                        <span className="clk-workflow-modal-count">
                            {currentIndex + 1} / {steps.length}
                        </span>
                        <button
                            type="button"
                            className="clk-workflow-modal-nav-btn"
                            onClick={goNext}
                            disabled={currentIndex >= steps.length - 1}
                            aria-label="Next"
                        >
                            <Icon name="arrow" size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default WorkflowPreviewModal;
